
import { useMarketTicker } from '@/lib/hooks/usePerpMarket'
import { formatCurrency, formatPercentageDisplay, formatPnlColor, formatPnLDisplay, formatPriceDisplay } from '@/lib/utils/format'
import type { ClearinghouseState } from '@/lib/utils/hyper/types'
import { navigateToTradeWithSymbol } from '@/lib/utils/navigation.utils'
import { Box, HStack, Text, VStack } from '@chakra-ui/react'
import { useTranslations } from 'next-intl'

interface PositionDetailCardProps {
  position: ClearinghouseState['assetPositions'][0]['position']
}

export function PositionDetailCard({ position }: PositionDetailCardProps) {
  const t = useTranslations()

  const coin = position.coin.replace('-PERP', '')
  const entryPrice = Number.parseFloat(position.entryPx || '0')
  const liquidationPrice = Number.parseFloat(position.liquidationPx || '0')
  const positionValue = Number.parseFloat(position.positionValue || '0')
  const roe = Number.parseFloat(position.returnOnEquity || '0')

  // Get market ticker for current price
  const { data: marketTicker } = useMarketTicker(coin)

  const isProfitable = Number.parseFloat(position.unrealizedPnl) > 0
  const pnl = Number.parseFloat(position.unrealizedPnl)

  const handleGoTrade = async () => {
    navigateToTradeWithSymbol(coin)
  }

  // Use actual market price from useMarketTicker
  const marketPrice = marketTicker?.mark_price || marketTicker?.last_price || entryPrice

  // Format currency values using the proper crypto price formatter

  return (
    <Box >
      {/* Header */}
      <HStack align="center" justify="space-between" px={4}>
        <Text fontSize="xl" color="text.t1" fontWeight="bold">{t('trader.position.currentPosition')}</Text>
      </HStack>

      {/* Position Details Card */}
      <Box p={4}>
        <HStack wrap="wrap" border="1px solid" borderColor="white/8" rounded="2xl" bg="white/5" p={4}>
          {/* Position Details */}
          <VStack align="flex-start" flex={1} gap={4}>
            {/* Top Row */}
            <HStack w="full" align="flex-end" justify="space-between">
              <VStack align="flex-start" gap={2}>
                <Text fontSize="xs" color="text.t1/40" fontWeight="medium">{t('trader.position.entryPrice')}</Text>
                <Text fontSize="md" color="text.t1/80" fontWeight="medium">{formatPriceDisplay(entryPrice)}</Text>
              </VStack>

              <VStack align="flex-start" gap={2}>
                <Text fontSize="xs" color="text.t1/60" fontWeight="medium">{t('trader.position.pnl')}</Text>
                <Text fontSize="md" color={formatPnlColor(false, isProfitable)} fontWeight="medium">
                  {formatPnLDisplay(pnl)}
                </Text>
              </VStack>

              <VStack align="flex-start" gap={2}>
                <Text fontSize="xs" color="text.t1/60" fontWeight="medium">{t('trader.position.roe')}</Text>
                <Text fontSize="md" color={formatPnlColor(false, isProfitable)} fontWeight="medium">
                  {formatPercentageDisplay(roe * 100)}
                </Text>
              </VStack>
            </HStack>

            {/* Divider */}
            <Box w="full" h='1px' bg="white/10" />

            {/* Bottom Row */}
            <HStack w="full" align="flex-end" justify="space-between">
              <VStack align="flex-start" gap={0.5}>
                <Text fontSize="xs" color="text.t1/60" fontWeight="medium">{t('trader.position.amount')}</Text>
                <Text fontSize="xs" color="text.t1/80" fontWeight="medium">
                  {formatCurrency(Math.abs(positionValue))}
                </Text>
              </VStack>

              <VStack align="flex-end" gap={0.5}>
                <Text fontSize="xs" color="text.t1/60" fontWeight="medium">{t('trader.position.mktLiqPrice')}</Text>
                <HStack>
                  <Text fontSize="xs" color="text.t1/80" fontWeight="medium">
                    {formatPriceDisplay(marketPrice)}
                    {' '}
                    /
                    {' '}
                  </Text>
                  <Text fontSize="xs" color="loss" fontWeight="medium">
                    {liquidationPrice ? formatPriceDisplay(liquidationPrice) : '- -'}
                  </Text>
                </HStack>
              </VStack>
            </HStack>
          </VStack>
        </HStack>
      </Box>
    </Box>
  )
}
